import type { JsonValue, Sha256Digest } from './contracts';
import { assertJsonValue } from './contracts';

function canonicalise(value: JsonValue): string {
    if (value === null) return 'null';
    if (typeof value === 'string' || typeof value === 'boolean') return JSON.stringify(value);
    if (typeof value === 'number') return JSON.stringify(Object.is(value, -0) ? 0 : value);
    if (Array.isArray(value)) return `[${value.map(canonicalise).join(',')}]`;

    const entries = Object.keys(value)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${canonicalise(value[key] as JsonValue)}`);
    return `{${entries.join(',')}}`;
}

/** Lexicographic-key canonical JSON for the strict JSON subset in contracts. */
export function canonicalJson(value: unknown): string {
    assertJsonValue(value);
    return canonicalise(value);
}

/**
 * Validate before cloning so JSON.stringify cannot silently erase undefined,
 * functions, browser objects, or other non-portable values. Canonicalisation
 * also gives every successful clone a deterministic key order.
 */
export function cloneCanonicalJson<T>(value: T, path = '$'): T {
    assertJsonValue(value, path);
    return JSON.parse(canonicalise(value)) as T;
}

// Pure SHA-256 keeps the portable core independent of Node crypto and browser
// WebCrypto globals. It operates on canonical UTF-8 bytes and returns lowercase
// content addresses with an explicit algorithm prefix.
const SHA256_INITIAL = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
] as const;

const SHA256_K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
] as const;

function rotateRight(value: number, amount: number): number {
    return (value >>> amount) | (value << (32 - amount));
}

function utf8Bytes(value: string): number[] {
    const bytes: number[] = [];
    for (let index = 0; index < value.length; index += 1) {
        let codePoint = value.charCodeAt(index);
        if (codePoint >= 0xd800 && codePoint <= 0xdbff) {
            const next = value.charCodeAt(index + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (next - 0xdc00);
                index += 1;
            } else {
                codePoint = 0xfffd;
            }
        } else if (codePoint >= 0xdc00 && codePoint <= 0xdfff) {
            codePoint = 0xfffd;
        }

        if (codePoint <= 0x7f) {
            bytes.push(codePoint);
        } else if (codePoint <= 0x7ff) {
            bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
        } else if (codePoint <= 0xffff) {
            bytes.push(
                0xe0 | (codePoint >>> 12),
                0x80 | ((codePoint >>> 6) & 0x3f),
                0x80 | (codePoint & 0x3f),
            );
        } else {
            bytes.push(
                0xf0 | (codePoint >>> 18),
                0x80 | ((codePoint >>> 12) & 0x3f),
                0x80 | ((codePoint >>> 6) & 0x3f),
                0x80 | (codePoint & 0x3f),
            );
        }
    }
    return bytes;
}

export function sha256Text(value: string): Sha256Digest {
    const bytes = utf8Bytes(value);
    const bitLength = bytes.length * 8;
    bytes.push(0x80);
    while ((bytes.length % 64) !== 56) bytes.push(0);
    const high = Math.floor(bitLength / 0x100000000);
    const low = bitLength >>> 0;
    bytes.push(
        (high >>> 24) & 0xff, (high >>> 16) & 0xff, (high >>> 8) & 0xff, high & 0xff,
        (low >>> 24) & 0xff, (low >>> 16) & 0xff, (low >>> 8) & 0xff, low & 0xff,
    );

    const hash: number[] = [...SHA256_INITIAL];
    const schedule = new Array<number>(64).fill(0);
    for (let offset = 0; offset < bytes.length; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            const byteOffset = offset + index * 4;
            schedule[index] = (
                ((bytes[byteOffset] as number) << 24)
                | ((bytes[byteOffset + 1] as number) << 16)
                | ((bytes[byteOffset + 2] as number) << 8)
                | (bytes[byteOffset + 3] as number)
            ) >>> 0;
        }
        for (let index = 16; index < 64; index += 1) {
            const before15 = schedule[index - 15] as number;
            const before2 = schedule[index - 2] as number;
            const sigma0 = rotateRight(before15, 7) ^ rotateRight(before15, 18) ^ (before15 >>> 3);
            const sigma1 = rotateRight(before2, 17) ^ rotateRight(before2, 19) ^ (before2 >>> 10);
            schedule[index] = (
                (schedule[index - 16] as number)
                + sigma0
                + (schedule[index - 7] as number)
                + sigma1
            ) >>> 0;
        }

        let a = hash[0] as number;
        let b = hash[1] as number;
        let c = hash[2] as number;
        let d = hash[3] as number;
        let e = hash[4] as number;
        let f = hash[5] as number;
        let g = hash[6] as number;
        let h = hash[7] as number;

        for (let index = 0; index < 64; index += 1) {
            const sigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temp1 = (h + sigma1 + choose + (SHA256_K[index] as number) + (schedule[index] as number)) >>> 0;
            const sigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (sigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }

        hash[0] = ((hash[0] as number) + a) >>> 0;
        hash[1] = ((hash[1] as number) + b) >>> 0;
        hash[2] = ((hash[2] as number) + c) >>> 0;
        hash[3] = ((hash[3] as number) + d) >>> 0;
        hash[4] = ((hash[4] as number) + e) >>> 0;
        hash[5] = ((hash[5] as number) + f) >>> 0;
        hash[6] = ((hash[6] as number) + g) >>> 0;
        hash[7] = ((hash[7] as number) + h) >>> 0;
    }

    const hex = hash.map((part) => part.toString(16).padStart(8, '0')).join('');
    return `sha256:${hex}`;
}

export function hashCanonicalJson(value: unknown): Sha256Digest {
    return sha256Text(canonicalJson(value));
}
